import { type FC, type ReactNode } from 'react';
import { useIntl, FormattedMessage, type IntlShape } from 'dibs-react-intl';
import { SearchTag } from 'dibs-elements/exports/SearchTag.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { type FilterConfig, type FilterShape } from './filterConfig.ts';

import styles from './NftMarketActiveFilterTags.module.css';

const getSearchTags = (
    filterConfig: FilterConfig,
    state: FilterShape,
    formatMessage: IntlShape['formatMessage']
): Array<{ value: string; label: ReactNode }> => {
    const searchTags: Array<{ value: string; label: ReactNode }> = [];
    let filterKey: keyof FilterShape;
    for (filterKey in state) {
        if (state.hasOwnProperty(filterKey)) {
            const value = state[filterKey];
            if (value && filterKey !== 'sortBy') {
                const selectedValues = Array.isArray(value) ? value : [value];
                const filterDefinition = filterConfig[filterKey];

                selectedValues.forEach(selectedValue => {
                    const valueLabel = filterDefinition.options.find(
                        option => option.value === selectedValue
                    )?.label;

                    if (valueLabel) {
                        const filterName = formatMessage(valueLabel);

                        searchTags.push({
                            label: (
                                <>
                                    {formatMessage(filterDefinition.facetName)}
                                    {': '}
                                    <b>{filterName}</b>
                                </>
                            ),
                            value: selectedValue,
                        });
                    }
                });
            }
        }
    }

    return searchTags;
};

type Props = {
    state: FilterShape;
    filterConfig: FilterConfig;
    showClearAll: boolean;
    onClearAll: () => void;
    onDeleteFilter: (deletedValue: string) => void;
};

export const NftMarketActiveFilterTags: FC<Props> = ({
    state,
    filterConfig,
    showClearAll,
    onClearAll,
    onDeleteFilter,
}) => {
    const { formatMessage } = useIntl();
    const searchTags = getSearchTags(filterConfig, state, formatMessage);

    return (
        <div className={styles.container}>
            {searchTags.map(({ value, label }) => (
                <SearchTag tagSpacing="xSmall" onDelete={() => onDeleteFilter(value)} key={value}>
                    {label}
                </SearchTag>
            ))}
            {showClearAll ? (
                <Link onClick={onClearAll}>
                    <FormattedMessage
                        id="nft.market.filters.filterAndSort.clearAll"
                        defaultMessage="Clear All"
                    />
                </Link>
            ) : null}
        </div>
    );
};
